import { isR, isO, queue, registerCleanup } from './common';
import { expressionPool, onExpressionUpdate } from './expressions';
/**
 * A registry of reactive objects to their unique numeric index which serves as
 * an unique identifier.
 */
const ids = new WeakMap();
const computedIds = [];
/**
 * A registry of reactive objects to their property observers.
 */
const listeners = [];
/**
 * Gets the unique id of a given target.
 * @param target - The object to get the id of.
 * @returns
 */
const getId = (target) => ids.get(target);
/**
 * An index counter for the reactive objects.
 */
let index = -1;
/**
 * An index counter to identify watchers.
 */
let watchIndex = 0;
/**
 * The current key being tracked.
 */
let trackKey = 0;
/**
 * Array methods that modify the array.
 */
/**
 * A registry of dependencies for each tracked key.
 */
const trackedDependencies = [];
/**
 * A registry of dependencies that are being watched by a given watcher.
 */
const watchedDependencies = [];
const dependencyPool = [];
const arrayMutationWrappers = [];
const arrayMutations = {
    push: 1,
    pop: 1,
    shift: 1,
    unshift: 1,
    splice: 1,
    sort: 1,
    copyWithin: 1,
    fill: 1,
    reverse: 1,
};
/**
 * A map of child ids to their parents (a child can have multiple parents).
 */
const parents = [];
export function reactive(data) {
    if (typeof data === 'function') {
        const state = reactive({
            value: undefined,
        });
        computedIds[getId(state)] = true;
        watch(data, (value) => (state.value = value));
        return state;
    }
    // The data is already a reactive object, so return it.
    if (isR(data))
        return data;
    // Only valid objects can be reactive.
    if (!isO(data))
        throw Error('Expected object');
    // Create a new slot in the listeners registry and then store the relationship
    // of this object to its index.
    const id = ++index;
    listeners[id] = {};
    // Create the actual reactive proxy.
    const proxy = new Proxy(data, proxyHandler);
    // let the ids know about the index
    ids.set(data, id).set(proxy, id);
    return proxy;
}
/**
 *
 * @param parentId - The id of the parent object.
 * @param property - The property of the parent object.
 * @param value - The value of the property.
 * @returns
 */
function trackArray(id, key, target, value) {
    if (typeof value === 'function' &&
        arrayMutations[key]) {
        let wrappers = arrayMutationWrappers[id];
        if (!wrappers)
            wrappers = arrayMutationWrappers[id] = {};
        let wrapper = wrappers[key];
        if (!wrapper) {
            wrapper = (...args) => {
                const result = Reflect.apply(value, target, args);
                emitParents(id);
                return result;
            };
            wrappers[key] = wrapper;
        }
        return wrapper;
    }
    if (isComputed(value))
        return readComputed(value, id, key);
    if (key !== 'length' && typeof value !== 'function') {
        track(id, key);
    }
    return value;
}
const proxyHandler = {
    has(target, key) {
        if (key in api)
            return true;
        track(getId(target), key);
        return key in target;
    },
    get(target, key, receiver) {
        const id = getId(target);
        if (key in api)
            return api[key];
        const result = Reflect.get(target, key, receiver);
        let child;
        if (isO(result) && !isR(result)) {
            child = createChild(result, id, key);
            target[key] = child;
        }
        const value = child ?? result;
        if (Array.isArray(target))
            return trackArray(id, key, target, value);
        if (isComputed(value))
            return readComputed(value, id, key);
        track(id, key);
        return value;
    },
    set(target, key, value, receiver) {
        const id = getId(target);
        const isNewProperty = !(key in target);
        const newReactive = isO(value) && !isR(value) ? createChild(value, id, key) : null;
        const oldValue = target[key];
        const newValue = newReactive ?? value;
        if (isR(newValue) && computedIds[getId(newValue)]) {
            linkParent(getId(newValue), id, key);
        }
        const didSucceed = Reflect.set(target, key, newValue, receiver);
        if (oldValue !== newValue && isR(oldValue) && isR(newValue)) {
            const oldParents = parents[getId(oldValue)];
            if (oldParents) {
                let index = -1;
                for (let i = 0; i < oldParents.length; i++) {
                    const [parent, property] = oldParents[i];
                    if (parent == id && property == key) {
                        index = i;
                        break;
                    }
                }
                if (index > -1)
                    oldParents.splice(index, 1);
            }
            linkParent(getId(newValue), id, key);
        }
        emit(id, key, value, oldValue, isNewProperty || (key === 'value' && computedIds[id]));
        if (Array.isArray(target) && key === 'length') {
            emitParents(id);
        }
        return didSucceed;
    },
};
/**
 *
 * @param child - Creates a child relationship
 * @param parent
 * @param key
 * @returns
 */
function createChild(child, parentId, key) {
    const r = reactive(child);
    linkParent(getId(child), parentId, key);
    return r;
}
function isComputed(value) {
    return isR(value) && computedIds[getId(value)];
}
function readComputed(value, parentId, key) {
    const computedId = getId(value);
    track(parentId, key);
    linkParent(computedId, parentId, key);
    track(computedId, 'value');
    return value.value;
}
function linkParent(childId, parentId, key) {
    const entries = parents[childId];
    if (entries) {
        for (let i = 0; i < entries.length; i++) {
            const [parent, property] = entries[i];
            if (parent === parentId && property === key)
                return;
        }
    }
    else {
        parents[childId] = [];
    }
    parents[childId].push([parentId, key]);
}
/**
 *
 * @param id - The reactive id that changed.
 * @param key - The property that changed.
 * @param newValue - The new value of the property.
 * @param oldValue - The old value of the property.
 */
function emit(id, key, newValue, oldValue, notifyParents) {
    const targetListeners = listeners[id];
    const propertyListeners = targetListeners[key];
    if (propertyListeners) {
        if (Array.isArray(propertyListeners)) {
            for (let i = 0; i < propertyListeners.length; i++) {
                propertyListeners[i](newValue, oldValue);
            }
        }
        else {
            propertyListeners(newValue, oldValue);
        }
    }
    if (notifyParents) {
        emitParents(id);
    }
}
function emitParents(id) {
    const parentEntries = parents[id];
    if (!parentEntries)
        return;
    for (let i = 0; i < parentEntries.length; i++) {
        const [parentId, property] = parentEntries[i];
        emit(parentId, property);
    }
}
function reactiveOn(property, callback) {
    addListener(listeners[getId(this)], property, callback);
}
function reactiveOff(property, callback) {
    removeListener(listeners[getId(this)], property, callback);
}
/**
 * The public reactive API for a reactive object.
 */
const api = {
    $on: reactiveOn,
    $off: reactiveOff,
};
/**
 * Track a reactive property as a dependency.
 * @param target
 * @param key
 */
function track(id, property) {
    if (!trackKey)
        return;
    trackedDependencies[trackKey].push(id, property);
}
/**
 * Begin tracking reactive dependencies.
 */
function startTracking() {
    trackedDependencies[++trackKey] = dependencyPool.pop() ?? [];
}
/**
 * Stop tracking reactive dependencies and register a callback for when any of
 * the tracked dependencies change.
 * @param callback - A function to re-run when dependencies change.
 */
function stopTracking(watchKey, callback) {
    const key = trackKey--;
    const deps = trackedDependencies[key];
    const previousDeps = watchedDependencies[watchKey];
    const previousLength = previousDeps?.length;
    if (previousLength && previousLength === deps.length) {
        let matched = true;
        for (let i = 0; i < previousLength; i++) {
            if (previousDeps[i] === deps[i])
                continue;
            matched = false;
            break;
        }
        if (matched) {
            watchedDependencies[watchKey] = previousDeps;
            deps.length = 0;
            dependencyPool.push(deps);
            trackedDependencies[key] = undefined;
            return;
        }
    }
    flushListeners(previousDeps, callback);
    for (let i = 0; i < deps.length; i += 2) {
        addListener(listeners[deps[i]], deps[i + 1], callback);
    }
    watchedDependencies[watchKey] = deps;
    trackedDependencies[key] = undefined;
}
/**
 * Removes a callback from the listeners registry for a given set of
 * dependencies.
 * @param deps - The dependencies to flush.
 * @param callback - The callback to remove.
 */
function flushListeners(deps, callback) {
    if (!deps)
        return;
    for (let i = 0; i < deps.length; i += 2) {
        removeListener(listeners[deps[i]], deps[i + 1], callback);
    }
    deps.length = 0;
    dependencyPool.push(deps);
}
function addListener(targetListeners, key, callback) {
    const slot = targetListeners[key];
    if (!slot) {
        targetListeners[key] = callback;
        return;
    }
    if (Array.isArray(slot)) {
        if (!slot.includes(callback))
            slot.push(callback);
        return;
    }
    if (slot !== callback)
        targetListeners[key] = [slot, callback];
}
function removeListener(targetListeners, key, callback) {
    const slot = targetListeners[key];
    if (!slot)
        return;
    if (Array.isArray(slot)) {
        const index = slot.indexOf(callback);
        if (index < 0)
            return;
        if (slot.length === 2) {
            targetListeners[key] = slot[index ? 0 : 1];
            return;
        }
        slot.splice(index, 1);
        return;
    }
    if (slot === callback) {
        delete targetListeners[key];
    }
}
export function watch(effect, afterEffect) {
    const watchKey = ++watchIndex;
    const isPointer = typeof effect === 'number';
    let rerun = queue(runEffect);
    function runEffect() {
        startTracking();
        const effectValue = isPointer
            ? expressionPool[effect]()
            : effect();
        stopTracking(watchKey, rerun);
        return afterEffect ? afterEffect(effectValue) : effectValue;
    }
    const stop = () => {
        flushListeners(watchedDependencies[watchKey], rerun);
        watchedDependencies[watchKey] = undefined;
        if (isPointer)
            onExpressionUpdate(effect);
        rerun = null;
    };
    if (!isPointer)
        registerCleanup(stop);
    if (isPointer)
        onExpressionUpdate(effect, runEffect);
    return [runEffect(), stop];
}
